/**
 * Форматтеры для отображения. Слой `lib` — никаких импортов Vue и никакого состояния:
 * эти функции зовут и из шаблонов, и из сторов, и из тестов.
 *
 * Локаль зафиксирована как `ru-RU` намеренно: панель одноязычная,
 * а «умный» подхват `navigator.language` дал бы разный вывод у разных админов
 * при одинаковых данных.
 */

const LOCALE = 'ru-RU'

/** Один общий инстанс на процесс: конструирование `Intl.*` дороже самого форматирования. */
const DATE_TIME_FORMAT = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const DATE_FORMAT = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const RELATIVE_FORMAT = new Intl.RelativeTimeFormat('ru', { numeric: 'auto' })

/** Заглушка для пустых и невалидных значений — единая по всему UI. */
const EMPTY = '—'

/**
 * Разбирает отформатированную дату на именованные части.
 *
 * Зачем не `format()`: `ru-RU` подмешивает эру («4 авг. 2026 **г.**, 17:45») и точку
 * в сокращении месяца. Части позволяют собрать компактную строку, не хардкодя
 * названия месяцев — они по-прежнему приходят из ICU.
 */
function dateParts(date: Date, formatter: Intl.DateTimeFormat): Record<string, string> {
  const result: Record<string, string> = {}
  for (const part of formatter.formatToParts(date)) {
    if (part.type !== 'literal') result[part.type] = part.value
  }
  return result
}

/** Парсит ISO-строку в `Date`, отсекая пустое и невалидное. */
function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Убирает точку у сокращённого месяца: «авг.» → «авг» (у «мая» точки и так нет). */
function trimMonth(month: string | undefined): string {
  return (month ?? '').replace(/\.$/, '')
}

/** «4 авг 2026, 17:45». Для null/пустой/невалидной строки — «—». */
export function formatDateTime(iso: string | null | undefined): string {
  const date = toDate(iso)
  if (!date) return EMPTY

  const p = dateParts(date, DATE_TIME_FORMAT)
  return `${p.day} ${trimMonth(p.month)} ${p.year}, ${p.hour}:${p.minute}`
}

/** «4 авг 2026». Для null/пустой/невалидной строки — «—». */
export function formatDate(iso: string | null | undefined): string {
  const date = toDate(iso)
  if (!date) return EMPTY

  const p = dateParts(date, DATE_FORMAT)
  return `${p.day} ${trimMonth(p.month)} ${p.year}`
}

/**
 * Шкала для `formatRelative`: `amount` — сколько единиц текущего уровня
 * укладывается в следующий. Недели пропущены сознательно: «3 недели назад»
 * читается хуже, чем «21 день назад», а между месяцем и днём пользы не несёт.
 */
const RELATIVE_DIVISIONS: ReadonlyArray<{ amount: number, unit: Intl.RelativeTimeFormatUnit }> = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 30.4375, unit: 'day' }, // средняя длина месяца: 365.25 / 12
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
]

/**
 * «5 минут назад» / «через 3 дня» / «вчера» — выбирается крупнейшая осмысленная единица.
 *
 * `numeric: 'auto'` даёт человеческие формы («вчера», «сейчас») вместо «1 день назад»,
 * ради чего этот форматтер и существует. Для null/невалидной строки — «—».
 */
export function formatRelative(iso: string | null | undefined): string {
  const date = toDate(iso)
  if (!date) return EMPTY

  let duration = (date.getTime() - Date.now()) / 1000

  for (const division of RELATIVE_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      // `|| 0` схлопывает -0: иначе «сейчас» рискует стать «0 секунд назад».
      return RELATIVE_FORMAT.format(Math.round(duration) || 0, division.unit)
    }
    duration /= division.amount
  }

  return EMPTY
}

/**
 * «Иванов Иван Иванович» из трёх полей участника.
 *
 * Склейка живёт здесь, а не в шаблоне, ровно потому, что отчество —
 * **скаляр**: у тех, у кого его нет, приезжает `''`, и наивное
 * `` `${last} ${first} ${middle}` `` даёт хвостовой пробел, который потом
 * ловится в поиске, в `title` и в отчёте импорта. Пустые части отбрасываются,
 * лишние пробелы внутри значений — тоже.
 *
 * Порядок «Фамилия Имя Отчество» зафиксирован: именно так список читают глазами
 * и именно так он сортируется на сервере (`order_by=last_name`).
 */
export function formatFullName(
  lastName: string,
  firstName: string,
  middleName?: string | null,
): string {
  const parts = [lastName, firstName, middleName ?? '']
    .map((part) => part.trim())
    .filter((part) => part !== '')

  return parts.length > 0 ? parts.join(' ') : EMPTY
}

/**
 * Компактная подпись устройства по User-Agent — **запасной вариант**.
 * Бэкенд присылает готовый `deviceLabel`, и он всегда приоритетнее:
 * серверный парсер полноценный, этот — упорядоченный список подстрок на десяток случаев.
 *
 * Порядок проверок значим: Edge и Opera содержат «Chrome», Android содержит «Linux»,
 * Chrome и Firefox под iOS маскируются в «CriOS»/«FxiOS».
 */
export function parseUserAgent(ua: string): string {
  const unknown = 'Неизвестное устройство'
  if (!ua) return unknown

  let browser = ''
  if (ua.includes('Edg')) browser = 'Edge'
  else if (ua.includes('OPR') || ua.includes('Opera')) browser = 'Opera'
  else if (ua.includes('YaBrowser')) browser = 'Яндекс.Браузер'
  else if (ua.includes('CriOS') || ua.includes('Chrome') || ua.includes('Chromium')) browser = 'Chrome'
  else if (ua.includes('FxiOS') || ua.includes('Firefox')) browser = 'Firefox'
  else if (ua.includes('Safari')) browser = 'Safari'

  let os = ''
  if (ua.includes('Windows')) os = 'Windows'
  else if (ua.includes('Android')) os = 'Android'
  else if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iPod')) os = 'iOS'
  else if (ua.includes('Mac OS X') || ua.includes('Macintosh')) os = 'macOS'
  else if (ua.includes('Linux')) os = 'Linux'

  if (browser && os) return `${browser} на ${os}`
  return browser || os || unknown
}

/**
 * Русская плюрализация: `pluralRu(n, ['сессия', 'сессии', 'сессий'])`.
 * Формы — для 1, 2 и 5 соответственно.
 *
 * Написано вручную, а не через `Intl.PluralRules`: правила совпадают,
 * но `PluralRules` возвращает категорию («one»/«few»/«many»), и её всё равно
 * пришлось бы отображать в те же три строки — лишний слой без выигрыша.
 */
export function pluralRu(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(Math.trunc(n))
  const mod10 = abs % 10
  const mod100 = abs % 100

  if (mod10 === 1 && mod100 !== 11) return forms[0]
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1]
  return forms[2]
}
